import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractErr, mediaUrl, storePublicBase } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Plus, Trash2, Save, Upload, Copy, Search } from "lucide-react";
import { GOOGLE_FONTS } from "@/lib/googleFonts";
import { WHY_US_LUCIDE_NAMES, WHY_US_EMOJIS } from "@/lib/whyUsIcons";
import { useFileUpload } from "@/lib/uploadProgress";
import { useMerchantAuth } from "@/lib/auth";
import { directCheckoutPath } from "@/lib/directCheckout";

function UploadImage({ kind, value, onChange, testid, hint }) {
  const uploadFile = useFileUpload();
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    setUploading(true);
    try {
      const data = await uploadFile("/merchant/media/upload", fd);
      onChange(data.url);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="flex items-center gap-x-3 gap-y-1 flex-wrap">
      {value && <img src={mediaUrl(value)} alt="" className="w-16 h-16 rounded-lg object-cover border border-slate-200" />}
      <div>
        <label className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 cursor-pointer" data-testid={testid}>
          <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : (value ? "Replace" : "Upload")}
          <input type="file" hidden accept="image/*" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
        </label>
        {hint ? <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">{hint}</p> : null}
      </div>
      {value && <button type="button" className="text-xs text-red-600" onClick={() => onChange("")}>Remove</button>}
    </div>
  );
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-1 flex items-center gap-2">
        <input type="color" className="h-10 w-12 rounded border border-slate-200 cursor-pointer" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} />
        <Input data-testid={testid} className="font-mono-nums" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      </div>
    </div>
  );
}

function IconPick({ value, onChange }) {
  return (
    <select className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm" value={value || "Leaf"} onChange={(e) => onChange(e.target.value)}>
      {WHY_US_LUCIDE_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
      {WHY_US_EMOJIS.map((n) => <option key={n} value={n}>{n}</option>)}
    </select>
  );
}

export default function AdminSingleProduct() {
  const { tenant } = useMerchantAuth();
  const [tab, setTab] = useState("landing");
  const [active, setActive] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [productQ, setProductQ] = useState("");
  const [picked, setPicked] = useState(null);
  const [variantId, setVariantId] = useState("");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get("/merchant/config/single-product").then(({ data }) => {
      setActive(Boolean(data.active));
      setCfg(data.single_product);
    }).catch((e) => toast.error(extractErr(e)));
    api.get("/merchant/products").then(({ data }) => setProducts(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  if (!cfg) return <div className="text-slate-500">Loading…</div>;
  if (!active) {
    return (
      <div className="max-w-2xl space-y-4">
        <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> App Store</Link>
        <Card className="p-8 text-center text-slate-600">Unlock <strong>Single Product Switch</strong> from the App Store to replace your homepage with a landing page.</Card>
      </div>
    );
  }

  const set = (patch) => setCfg({ ...cfg, ...patch });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/merchant/config/single-product", cfg);
      setCfg(data.single_product);
      toast.success("Landing page saved");
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setSaving(false);
    }
  };

  const patchList = (key, i, next) => {
    const list = [...(cfg[key] || [])];
    list[i] = { ...list[i], ...next };
    set({ [key]: list });
  };

  const slides = [...(cfg.hero_slides || [])];
  while (slides.length < 3) slides.push({ headline: "", tagline: "", body: "", price: "", compare_price: "", cta_text: "SHOP NOW →", cta_link: "/shop", image_url: "", image_url_mobile: "" });
  const patchSlide = (i, next) => {
    const nextSlides = slides.map((s, k) => (k === i ? { ...s, ...next } : s)).slice(0, 3);
    set({ hero_slides: nextSlides });
  };

  const checkoutPath = picked ? directCheckoutPath({ slug: picked.slug, variantId: variantId || undefined, qty }) : "";
  const checkoutFull = checkoutPath && tenant ? `${storePublicBase(tenant)}${checkoutPath}` : checkoutPath;
  const filteredProducts = products.filter((p) => {
    const q = productQ.trim().toLowerCase();
    if (!q) return true;
    return `${p.name || ""} ${p.slug || ""} ${p.sku || ""}`.toLowerCase().includes(q);
  });

  const copyLink = async () => {
    if (!checkoutPath) return;
    try {
      await navigator.clipboard.writeText(checkoutFull || checkoutPath);
      toast.success("Checkout link copied");
    } catch {
      toast.error("Could not copy — select the link and copy it");
    }
  };

  const applyLinkToLanding = () => {
    if (!checkoutPath) return;
    const nextSlides = slides.map((s) => ({ ...s, cta_link: checkoutPath })).slice(0, 3);
    set({
      hero_slides: nextSlides,
      about_cta_link: checkoutPath,
      about_cta2_link: checkoutPath,
      final_cta_link: checkoutPath,
      final_cta2_link: checkoutPath,
    });
    toast.success("Landing Shop Now buttons now use this checkout link. Save to publish.");
    setTab("landing");
  };

  const pickProduct = (p) => {
    setPicked(p);
    const first = (p.variants || [])[0];
    setVariantId(first?.id || "");
    setQty(1);
  };

  return (
    <div className="max-w-3xl space-y-5">
      <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1" data-testid="sp-back"><ArrowLeft className="w-4 h-4" /> App Store</Link>
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Single Product Switch</h1>
        <p className="text-sm text-slate-500 mt-1">This landing page replaces only the storefront homepage. Use Settings to generate a direct checkout link so shoppers skip the product and cart pages.</p>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        <button type="button" data-testid="sp-tab-landing" onClick={() => setTab("landing")} className={`px-4 py-2 text-sm font-medium ${tab === "landing" ? "text-slate-900 border-b-2 border-slate-900" : "text-slate-500"}`}>Landing page</button>
        <button type="button" data-testid="sp-tab-settings" onClick={() => setTab("settings")} className={`px-4 py-2 text-sm font-medium ${tab === "settings" ? "text-slate-900 border-b-2 border-slate-900" : "text-slate-500"}`}>Settings</button>
      </div>

      {tab === "settings" && (
        <Card className="p-6 space-y-4 border-slate-200/70" data-testid="sp-settings">
          <div>
            <h2 className="font-semibold text-slate-900">Direct checkout link</h2>
            <p className="text-sm text-slate-500 mt-1">Search a product, generate a checkout URL, and paste it into landing buttons. Shoppers go straight to checkout — no product page or cart.</p>
          </div>
          <div>
            <Label>Search products</Label>
            <div className="mt-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9" data-testid="sp-product-search" value={productQ} onChange={(e) => setProductQ(e.target.value)} placeholder="Search by name, slug, or SKU" />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="sp-product-list">
            {filteredProducts.length === 0 && <div className="p-3 text-sm text-slate-500">No products match that search.</div>}
            {filteredProducts.map((p) => (
              <button
                key={p.id}
                type="button"
                data-testid={`sp-product-${p.id}`}
                onClick={() => pickProduct(p)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${picked?.id === p.id ? "bg-slate-100" : ""}`}
              >
                <div className="font-medium text-slate-900 truncate">{p.name}</div>
                <div className="text-xs text-slate-500 font-mono-nums truncate">{p.slug}{p.is_active === false ? " · hidden" : ""}</div>
              </button>
            ))}
          </div>
          {picked && (
            <div className="space-y-3 rounded-lg border border-slate-200 p-3" data-testid="sp-checkout-link-box">
              <div className="text-sm text-slate-700">Selected: <strong>{picked.name}</strong></div>
              {(picked.variants || []).length > 0 && (
                <div>
                  <Label>Variant</Label>
                  <select className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="sp-checkout-variant" value={variantId} onChange={(e) => setVariantId(e.target.value)}>
                    {(picked.variants || []).map((v) => (
                      <option key={v.id} value={v.id}>{v.name || v.sku || v.id}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <Label>Quantity</Label>
                <Input className="mt-1 max-w-xs" type="number" min={1} max={99} data-testid="sp-checkout-qty" value={qty} onChange={(e) => setQty(e.target.value)} />
              </div>
              <div>
                <Label>Link for landing buttons</Label>
                <Input className="mt-1 font-mono-nums" readOnly data-testid="sp-checkout-path" value={checkoutPath} />
              </div>
              <div>
                <Label>Full storefront URL</Label>
                <Input className="mt-1 font-mono-nums" readOnly data-testid="sp-checkout-full" value={checkoutFull} />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" data-testid="sp-copy-checkout-link" onClick={copyLink}><Copy className="w-4 h-4 mr-1" /> Copy link</Button>
                <Button type="button" className="bg-violet-600 hover:bg-violet-700" data-testid="sp-apply-checkout-link" onClick={applyLinkToLanding}>Use on landing Shop Now buttons</Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {tab === "landing" && (
        <>
      <Card className="p-6 space-y-4 border-slate-200/70">
        <h2 className="font-semibold text-slate-900">Colors & fonts</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <ColorField label="Background" value={cfg.bg_color} onChange={(v) => set({ bg_color: v })} testid="sp-bg" />
          <ColorField label="Heading color" value={cfg.heading_color} onChange={(v) => set({ heading_color: v })} testid="sp-heading-color" />
          <ColorField label="Body color" value={cfg.body_color} onChange={(v) => set({ body_color: v })} testid="sp-body-color" />
          <ColorField label="Accent" value={cfg.accent_color} onChange={(v) => set({ accent_color: v })} testid="sp-accent" />
          <ColorField label="Button background" value={cfg.button_bg} onChange={(v) => set({ button_bg: v })} testid="sp-btn-bg" />
          <ColorField label="Button text" value={cfg.button_text} onChange={(v) => set({ button_text: v })} testid="sp-btn-text" />
          <ColorField label="Muted / icon circles" value={cfg.muted_bg} onChange={(v) => set({ muted_bg: v })} testid="sp-muted" />
          <ColorField label="Card background" value={cfg.card_bg} onChange={(v) => set({ card_bg: v })} testid="sp-card" />
          <ColorField label="Star color" value={cfg.star_color} onChange={(v) => set({ star_color: v })} testid="sp-star" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Heading font</Label>
            <select className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="sp-heading-font" value={cfg.heading_font || "Playfair Display"} onChange={(e) => set({ heading_font: e.target.value })}>
              {GOOGLE_FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <Label>Body font</Label>
            <select className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="sp-body-font" value={cfg.body_font || "Inter"} onChange={(e) => set({ body_font: e.target.value })}>
              {GOOGLE_FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
        </div>
      </Card>

      <Card className="p-6 space-y-4 border-slate-200/70">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">Hero slider (3 slides)</h2>
          <div className="flex items-center gap-2"><Label>Show</Label><Switch checked={cfg.show_hero !== false} onCheckedChange={(v) => set({ show_hero: v })} /></div>
        </div>
        <ColorField label="Section background" value={cfg.hero_bg} onChange={(v) => set({ hero_bg: v })} testid="sp-hero-bg" />
        {slides.slice(0, 3).map((sl, i) => (
          <div key={i} className="rounded-lg border border-slate-200 p-4 space-y-3" data-testid={`sp-hero-slide-${i}`}>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Slide {i + 1}</div>
            <Input data-testid={i === 0 ? "sp-hero-headline" : undefined} value={sl.headline || ""} onChange={(e) => patchSlide(i, { headline: e.target.value })} placeholder="Headline" />
            <Input value={sl.tagline || ""} onChange={(e) => patchSlide(i, { tagline: e.target.value })} placeholder="Tagline" />
            <Textarea value={sl.body || ""} onChange={(e) => patchSlide(i, { body: e.target.value })} rows={3} />
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Price</Label><Input className="mt-1" value={sl.price || ""} onChange={(e) => patchSlide(i, { price: e.target.value })} /></div>
              <div><Label>Compare / strike-through</Label><Input className="mt-1" value={sl.compare_price || ""} onChange={(e) => patchSlide(i, { compare_price: e.target.value })} /></div>
              <div><Label>Button text</Label><Input className="mt-1" value={sl.cta_text || ""} onChange={(e) => patchSlide(i, { cta_text: e.target.value })} /></div>
              <div><Label>Button link</Label><Input className="mt-1 font-mono-nums" value={sl.cta_link || ""} onChange={(e) => patchSlide(i, { cta_link: e.target.value })} placeholder="/shop or /product/your-slug" /></div>
            </div>
            <div><Label>Image (desktop)</Label><div className="mt-1"><UploadImage kind="hero" testid={`sp-hero-desktop-${i}`} hint="Recommended 1200×1200. Shown from 768px up." value={sl.image_url} onChange={(url) => patchSlide(i, { image_url: url })} /></div></div>
            <div><Label>Image (mobile)</Label><div className="mt-1"><UploadImage kind="hero" testid={`sp-hero-mobile-${i}`} hint="Recommended 800×1000 portrait. Used below 768px." value={sl.image_url_mobile} onChange={(url) => patchSlide(i, { image_url_mobile: url })} /></div></div>
          </div>
        ))}
      </Card>

      <Card className="p-6 space-y-4 border-slate-200/70">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">Features</h2>
          <Switch checked={cfg.show_features !== false} onCheckedChange={(v) => set({ show_features: v })} />
        </div>
        <ColorField label="Section background" value={cfg.features_bg} onChange={(v) => set({ features_bg: v })} testid="sp-features-bg" />
        <Input value={cfg.features_sub || ""} onChange={(e) => set({ features_sub: e.target.value })} />
        {(cfg.features || []).map((f, i) => (
          <div key={i} className="rounded-lg border border-slate-100 p-3 space-y-2">
            <IconPick value={f.icon} onChange={(icon) => patchList("features", i, { icon })} />
            <Input value={f.title || ""} onChange={(e) => patchList("features", i, { title: e.target.value })} />
            <Textarea rows={2} value={f.body || ""} onChange={(e) => patchList("features", i, { body: e.target.value })} />
          </div>
        ))}
      </Card>

      <Card className="p-6 space-y-4 border-slate-200/70">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">About</h2>
          <Switch checked={cfg.show_about !== false} onCheckedChange={(v) => set({ show_about: v })} />
        </div>
        <ColorField label="Section background" value={cfg.about_bg} onChange={(v) => set({ about_bg: v })} testid="sp-about-bg" />
        <Input value={cfg.about_heading || ""} onChange={(e) => set({ about_heading: e.target.value })} />
        <Textarea rows={4} value={cfg.about_body || ""} onChange={(e) => set({ about_body: e.target.value })} />
        <div className="grid sm:grid-cols-2 gap-3">
          <Input value={cfg.about_cta_text || ""} onChange={(e) => set({ about_cta_text: e.target.value })} />
          <Input className="font-mono-nums" value={cfg.about_cta_link || ""} onChange={(e) => set({ about_cta_link: e.target.value })} />
          <Input value={cfg.about_cta2_text || ""} onChange={(e) => set({ about_cta2_text: e.target.value })} />
          <Input className="font-mono-nums" value={cfg.about_cta2_link || ""} onChange={(e) => set({ about_cta2_link: e.target.value })} />
        </div>
        <div><Label>About image (desktop)</Label><div className="mt-1"><UploadImage kind="hero" testid="sp-about-desktop" hint="Square product photo, ~1000×1000." value={cfg.about_image_url} onChange={(url) => set({ about_image_url: url })} /></div></div>
        <div><Label>About image (mobile)</Label><div className="mt-1"><UploadImage kind="hero" testid="sp-about-mobile" hint="Optional portrait crop for phones." value={cfg.about_image_url_mobile} onChange={(url) => set({ about_image_url_mobile: url })} /></div></div>
      </Card>

      <Card className="p-6 space-y-4 border-slate-200/70">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">Ingredients</h2>
          <Switch checked={cfg.show_ingredients !== false} onCheckedChange={(v) => set({ show_ingredients: v })} />
        </div>
        <ColorField label="Section background" value={cfg.ingredients_bg} onChange={(v) => set({ ingredients_bg: v })} testid="sp-ingredients-bg" />
        <Input value={cfg.ingredients_heading || ""} onChange={(e) => set({ ingredients_heading: e.target.value })} />
        {(cfg.ingredients || []).map((ing, i) => (
          <div key={i} className="grid sm:grid-cols-2 gap-2">
            <IconPick value={ing.icon} onChange={(icon) => patchList("ingredients", i, { icon })} />
            <Input value={ing.label || ""} onChange={(e) => patchList("ingredients", i, { label: e.target.value })} />
          </div>
        ))}
      </Card>

      <Card className="p-6 space-y-4 border-slate-200/70">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">Testimonials</h2>
          <div className="flex items-center gap-2">
            <Switch checked={cfg.show_testimonials !== false} onCheckedChange={(v) => set({ show_testimonials: v })} />
            {(cfg.testimonials || []).length < 8 && (
              <Button type="button" size="sm" variant="outline" onClick={() => set({ testimonials: [...(cfg.testimonials || []), { name: "Customer", quote: "", rating: 5, image_url: "" }] })}>
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            )}
          </div>
        </div>
        <ColorField label="Section background" value={cfg.testimonials_bg} onChange={(v) => set({ testimonials_bg: v })} testid="sp-testimonials-bg" />
        <Input value={cfg.testimonials_heading || ""} onChange={(e) => set({ testimonials_heading: e.target.value })} />
        {(cfg.testimonials || []).map((t, i) => (
          <div key={i} className="rounded-lg border border-slate-100 p-3 space-y-2">
            <div className="flex justify-end">
              <button type="button" className="text-red-600" onClick={() => set({ testimonials: cfg.testimonials.filter((_, k) => k !== i) })}><Trash2 className="w-4 h-4" /></button>
            </div>
            <Input value={t.name || ""} onChange={(e) => patchList("testimonials", i, { name: e.target.value })} placeholder="Name" />
            <Textarea rows={2} value={t.quote || ""} onChange={(e) => patchList("testimonials", i, { quote: e.target.value })} />
            <Input type="number" min={1} max={5} value={t.rating || 5} onChange={(e) => patchList("testimonials", i, { rating: Number(e.target.value) })} />
            <UploadImage kind="hero" testid={`sp-testimonial-${i}`} hint="Circular portrait, ~400×400." value={t.image_url} onChange={(url) => patchList("testimonials", i, { image_url: url })} />
          </div>
        ))}
      </Card>

      <Card className="p-6 space-y-4 border-slate-200/70">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">Final call to action</h2>
          <Switch checked={cfg.show_final !== false} onCheckedChange={(v) => set({ show_final: v })} />
        </div>
        <ColorField label="Section background" value={cfg.final_bg} onChange={(v) => set({ final_bg: v })} testid="sp-final-bg" />
        <Input value={cfg.final_heading || ""} onChange={(e) => set({ final_heading: e.target.value })} />
        <Textarea rows={2} value={cfg.final_sub || ""} onChange={(e) => set({ final_sub: e.target.value })} />
        <div className="grid sm:grid-cols-2 gap-3">
          <Input value={cfg.final_cta_text || ""} onChange={(e) => set({ final_cta_text: e.target.value })} />
          <Input className="font-mono-nums" value={cfg.final_cta_link || ""} onChange={(e) => set({ final_cta_link: e.target.value })} />
          <Input value={cfg.final_cta2_text || ""} onChange={(e) => set({ final_cta2_text: e.target.value })} />
          <Input className="font-mono-nums" value={cfg.final_cta2_link || ""} onChange={(e) => set({ final_cta2_link: e.target.value })} />
        </div>
        <div><Label>Final image (desktop)</Label><div className="mt-1"><UploadImage kind="hero" testid="sp-final-desktop" value={cfg.final_image_url} onChange={(url) => set({ final_image_url: url })} /></div></div>
        <div><Label>Final image (mobile)</Label><div className="mt-1"><UploadImage kind="hero" testid="sp-final-mobile" hint="Optional mobile crop." value={cfg.final_image_url_mobile} onChange={(url) => set({ final_image_url_mobile: url })} /></div></div>
      </Card>

      <Card className="p-6 space-y-4 border-slate-200/70">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">Stats (just above footer)</h2>
          <Switch checked={cfg.show_stats !== false} onCheckedChange={(v) => set({ show_stats: v })} />
        </div>
        <p className="text-xs text-slate-500">Shown at the end of the landing page, immediately above the store footer.</p>
        <ColorField label="Section background" value={cfg.stats_bg} onChange={(v) => set({ stats_bg: v })} testid="sp-stats-bg" />
        <Input value={cfg.stats_heading || ""} onChange={(e) => set({ stats_heading: e.target.value })} />
        <Input value={cfg.stats_sub || ""} onChange={(e) => set({ stats_sub: e.target.value })} />
        {(cfg.stats || []).map((s, i) => (
          <div key={i} className="grid sm:grid-cols-2 gap-2">
            <Input value={s.value || ""} onChange={(e) => patchList("stats", i, { value: e.target.value })} />
            <Input value={s.label || ""} onChange={(e) => patchList("stats", i, { label: e.target.value })} />
          </div>
        ))}
      </Card>

      <Button onClick={save} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="sp-save">
        <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save landing page"}
      </Button>
        </>
      )}
    </div>
  );
}
